import React, { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { initNative } from "@/lib/native";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Reader from "@/pages/Reader";
import Bookmarks from "@/pages/Bookmarks";
import Settings from "@/pages/Settings";
import Badges from "@/pages/Badges";
import Groups from "@/pages/Groups";
import GroupDetail from "@/pages/GroupDetail";
import Leaderboard from "@/pages/Leaderboard";
import Teacher from "@/pages/Teacher";
import OfflineIndicator from "@/components/OfflineIndicator";
import { ThemeProvider } from "@/context/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Helper function to get auth token
export const getAuthToken = () => {
  const cachedUser = localStorage.getItem('quran_user');
  if (cachedUser) {
    const parsed = JSON.parse(cachedUser);
    return parsed.token;
  }
  return null;
};

// Helper function for authenticated API calls
export const authFetch = async (url, options = {}) => {
  const token = getAuthToken();
  const headers = {
    ...options.headers,
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return fetch(url, {
    ...options,
    headers,
    credentials: 'include'
  });
};

// Auth context with global state
export const AuthContext = createContext(null);

// Auth Provider - manages auth state globally
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check auth status on mount and when user changes
  const checkAuth = async () => {
    try {
      // First check localStorage for cached user
      const cachedUser = localStorage.getItem('quran_user');
      const token = cachedUser ? JSON.parse(cachedUser).token : null;

      if (cachedUser) {
        const parsed = JSON.parse(cachedUser);
        setUser(parsed);
        setIsAuthenticated(true);
      }

      // No cached token AND no cookie session → don't bother hitting the backend.
      // Hitting `/auth/me` here would return 401 and any race with a concurrent
      // login() would wipe the freshly-set credentials.
      if (!token) {
        setIsLoading(false);
        return;
      }

      // Verify token with backend (Bearer for JWT users; cookie also sent for OAuth).
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const userData = await response.json();
        // Preserve the cached JWT — `/auth/me` does not echo it back.
        const merged = { ...userData, token };
        // Re-read localStorage in case login() ran concurrently and replaced
        // the cached token with a newer one — keep the newer one.
        const latestRaw = localStorage.getItem('quran_user');
        const latestToken = latestRaw ? JSON.parse(latestRaw).token : null;
        if (latestToken && latestToken !== token) {
          // A newer login won the race — leave its state intact.
          return;
        }
        setUser(merged);
        setIsAuthenticated(true);
        localStorage.setItem('quran_user', JSON.stringify(merged));
      } else {
        // Token is no longer valid. But only clear if no newer login arrived.
        const latestRaw = localStorage.getItem('quran_user');
        const latestToken = latestRaw ? JSON.parse(latestRaw).token : null;
        if (latestToken && latestToken !== token) return;
        setUser(null);
        setIsAuthenticated(false);
        localStorage.removeItem('quran_user');
      }
    } catch (error) {
      console.error('Auth check error:', error);
      // Keep cached user if network error
      const cachedUser = localStorage.getItem('quran_user');
      if (cachedUser) {
        setUser(JSON.parse(cachedUser));
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem('quran_user', JSON.stringify(userData));
  };

  const updateUser = (updatedFields) => {
    setUser((prev) => {
      if (!prev) return prev;
      const merged = { ...prev, ...updatedFields };
      localStorage.setItem('quran_user', JSON.stringify(merged));
      return merged;
    });
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {
      console.error('Logout error:', e);
    }
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('quran_user');
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, login, logout, checkAuth, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for auth
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-islamic-blue-950 flex items-center justify-center">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // iOS Safari bug: passing the full `location` object into history state
    // can cause `DataCloneError: The object can not be cloned` on signup /
    // signin redirects (location.state can self-reference across redirects
    // and Safari's structuredClone bails out). Store only the pathname —
    // a string is always cloneable.
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }

  return children;
};

// App Router
const AppRouter = () => {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/reader/:surahNumber" element={<ProtectedRoute><Reader /></ProtectedRoute>} />
      <Route path="/bookmarks" element={<ProtectedRoute><Bookmarks /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/badges" element={<ProtectedRoute><Badges /></ProtectedRoute>} />
      <Route path="/groups" element={<ProtectedRoute><Groups /></ProtectedRoute>} />
      <Route path="/groups/:groupId" element={<ProtectedRoute><GroupDetail /></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
      <Route path="/teacher" element={<ProtectedRoute><Teacher /></ProtectedRoute>} />
    </Routes>
  );
};

function App() {
  // Initialize native shell (status bar, splash, back-button) once.
  // No-op in the browser; only fires when running inside Capacitor.
  useEffect(() => {
    initNative();
  }, []);

  return (
    <div className="min-h-screen bg-islamic-blue-950 islamic-pattern">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <OfflineIndicator />
            <AppRouter />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
